/**
 * KolCarPage — קוֹל "The Car" (Editorial Calm). A status headline in the car's
 * own voice (grounded ONLY in real data), a vitals bento built from whatever
 * facts actually exist (OBD fuel/battery/engine + registry license validity —
 * never fabricated, never em-dash placeholders), then quiet rows for documents,
 * live scan, and a calm accident affordance. Reuses existing routes.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getVehicle, getVehicleEnrichment, getLatestObdScan, ApiError } from '@/lib/api';
import type { VehicleSummary, VehicleEnrichment } from '@/lib/api';
import type { ObdScanResult } from '@tendedloop/shared';
import { Orb } from '@/components/kol/Orb';
import { VoiceDock } from '@/components/kol/VoiceDock';
import { Icon, type IconName } from '@/components/kol/Icon';
import { VehicleSettingsSheet } from '@/components/VehicleSettingsSheet';
import { formatDocDate, expiryState } from '@/lib/kol/docs';

function cap(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

interface Vital {
  value: string;
  label: string;
  icon: IconName;
  warn?: boolean;
}

export function KolCarPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [vehicle, setVehicle] = useState<(VehicleSummary & { tenantId: string }) | null>(null);
  const [enrichment, setEnrichment] = useState<VehicleEnrichment | null>(null);
  const [scan, setScan] = useState<ObdScanResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setError(null);
    setLoading(true);
    (async () => {
      try {
        const v = await getVehicle(id);
        if (!cancelled) setVehicle(v);
      } catch (err) {
        // Never silently swallow — surface a visible error with a retry path.
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : t('common.error', { defaultValue: 'משהו השתבש' }));
          setLoading(false);
        }
        return;
      }
      getVehicleEnrichment(id)
        .then((e) => !cancelled && setEnrichment(e))
        .catch(() => undefined);
      getLatestObdScan(id)
        .then((s) => !cancelled && setScan(s))
        .catch(() => undefined);
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [id, retry, t]);

  const fuel = scan?.pids.find((p) => p.key === 'fuelLevel')?.value;
  const volts = scan?.pids.find((p) => p.key === 'controlModuleVoltage')?.value;
  const dtcCount = scan ? scan.storedDtcs.length : null;
  const engineOk = dtcCount === 0;
  // Only "attention" when a scan actually found trouble — never just because
  // we have no scan yet (null !== 0 must not read as a problem).
  const hasIssue = dtcCount != null && dtcCount > 0;
  const regExpired = expiryState(enrichment?.registrationValidUntil ?? null) === 'expired';
  // Orb shows attention for a real engine fault OR an expired registration.
  const needsAttention = hasIssue || regExpired;

  // Spoken summary — assembled only from facts we actually have.
  const parts: string[] = [];
  if (enrichment?.registrationValidUntil) {
    const regDate = formatDocDate(enrichment.registrationValidUntil) ?? enrichment.registrationValidUntil;
    parts.push(
      regExpired
        ? t('kol.car.reg_expired', { defaultValue: 'הרישיון שלי פג תוקף ב-{{d}}. כדאי לחדש בהקדם.', d: regDate })
        : t('kol.car.reg', { defaultValue: 'הרישיון שלי בתוקף עד {{d}}.', d: regDate }),
    );
  }
  if (scan) {
    if (typeof fuel === 'number')
      parts.push(t('kol.car.fuel', { defaultValue: 'נשארו {{f}}% דלק.', f: Math.round(fuel) }));
    parts.push(
      engineOk
        ? t('kol.car.engine_ok', { defaultValue: 'המנוע תקין.' })
        : t('kol.car.engine_codes', { defaultValue: 'נקראו {{n}} קודי תקלה.', n: dtcCount }),
    );
  }
  const summary = parts.length
    ? parts.join(' ')
    : t('kol.car.unknown', { defaultValue: 'אני יודע את המסמכים, המפרט והתאריכים שלי.' });

  // Vitals bento — only real values; nothing is invented, nothing shows "—".
  const vitals: Vital[] = [];
  if (typeof fuel === 'number')
    vitals.push({ value: `${Math.round(fuel)}%`, label: t('kol.stat.fuel', { defaultValue: 'דלק' }), icon: 'fuel' });
  if (typeof volts === 'number')
    vitals.push({ value: `${volts}V`, label: t('kol.stat.batt', { defaultValue: 'מצבר' }), icon: 'battery' });
  if (scan)
    vitals.push({
      value: engineOk ? t('kol.stat.ok', { defaultValue: 'תקין' }) : `${dtcCount}`,
      label: engineOk
        ? t('kol.stat.engine_ok', { defaultValue: 'מנוע · 0 תקלות' })
        : t('kol.stat.engine', { defaultValue: 'מנוע' }),
      icon: 'engine',
      warn: !engineOk,
    });
  if (enrichment?.registrationValidUntil)
    vitals.push({
      value: formatDocDate(enrichment.registrationValidUntil) ?? enrichment.registrationValidUntil,
      label: regExpired
        ? t('kol.stat.reg_expired', { defaultValue: 'רישיון פג תוקף' })
        : t('kol.stat.reg', { defaultValue: 'רישיון בתוקף' }),
      icon: 'calendar',
      warn: regExpired,
    });

  const carName = vehicle?.customLabel?.trim() || (vehicle ? `${cap(vehicle.oem)} ${cap(vehicle.model)}` : '…');

  return (
    <div className="kol-grain font-kol min-h-screen bg-kol-canvas text-kol-ink flex flex-col relative" dir={i18n.dir()}>
      <div className="relative z-[1] flex items-center justify-between px-[22px] pt-[max(18px,env(safe-area-inset-top))] pb-2">
        <div className="min-w-0">
          <h1 className="font-kol-display text-[26px] font-medium tracking-tight truncate leading-tight">{carName}</h1>
          {vehicle && (
            <div className="text-[15px] text-kol-ink-faint tabular-nums tracking-[0.04em]">
              {vehicle.licensePlate ? `${vehicle.licensePlate} · ` : ''}
              {vehicle.year}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {vehicle && (
            <button
              type="button"
              aria-label={t('vehicle.settings_aria', { defaultValue: 'הגדרות הרכב' })}
              onClick={() => setSettingsOpen(true)}
              className="w-11 h-11 rounded-full border border-kol-line bg-kol-card-warm grid place-items-center text-kol-ink-soft shadow-kol-1"
            >
              <Icon name="sliders" size={20} />
            </button>
          )}
          <button
            type="button"
            aria-label={t('common.back', { defaultValue: 'חזרה' })}
            onClick={() => navigate('/kol')}
            className="w-11 h-11 rounded-full border border-kol-line bg-kol-card-warm grid place-items-center text-kol-ink-soft shadow-kol-1"
          >
            <Icon name="arrow" size={20} />
          </button>
        </div>
      </div>

      <div className="relative z-[1] flex-1 overflow-y-auto px-[22px] pb-2 flex flex-col gap-3.5">
        {error && (
          <div role="alert" className="rounded-[20px] bg-kol-clay-soft border border-[#ecd2c5] p-5 text-center">
            <p className="text-[16px] font-semibold text-kol-clay-ink">
              {t('kol.car.load_error', { defaultValue: 'לא הצלחנו לטעון את הרכב' })}
            </p>
            <p className="text-[15px] text-kol-clay-ink opacity-80 mt-1">{error}</p>
            <button
              type="button"
              onClick={() => setRetry((n) => n + 1)}
              className="mt-3 inline-flex items-center gap-2 rounded-full bg-kol-pine text-white font-semibold px-5 py-2.5 min-h-[44px]"
            >
              <Icon name="activity" size={18} className="stroke-white" />
              {t('common.retry', { defaultValue: 'נסו שוב' })}
            </button>
          </div>
        )}
        {!error && (
          <>
            {/* status headline — the car's voice, grounded, with a source */}
            <div
              className="rounded-[24px] p-5 text-white shadow-kol-3 relative overflow-hidden"
              style={{ background: 'linear-gradient(150deg, #2E7D5B 0%, #1F4E3D 100%)' }}
            >
              <div className="flex gap-4 items-start">
                <Orb state={needsAttention ? 'attention' : 'idle'} size={46} />
                <div className="flex-1">
                  <p className="font-kol-display text-[20px] leading-[1.4] font-medium">{summary}</p>
                  {scan && (
                    <div className="mt-3">
                      <span className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold rounded-[9px] px-2.5 py-1 bg-white/16 text-white">
                        <Icon name="activity" size={14} className="stroke-white" />
                        {t('kol.car.obd_src', { defaultValue: 'סריקת OBD' })}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* vitals bento — real values only */}
            {vitals.length > 0 && (
              <div className="grid grid-cols-2 gap-3">
                {vitals.map((v, i) => (
                  <div key={i} className="rounded-[20px] bg-kol-card border border-kol-line-soft shadow-kol-1 p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <div
                          className={`font-kol-display font-bold text-[26px] leading-none tabular-nums ${v.warn ? 'text-kol-clay-ink' : 'text-kol-pine'}`}
                        >
                          {v.value}
                        </div>
                        <div className="text-[15px] text-kol-ink-faint mt-1.5">{v.label}</div>
                      </div>
                      <Icon name={v.icon} size={21} className="text-kol-ink-faint opacity-80" />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Documents are not reachable from the car page — they live in the one
            global wallet (/kol/documents) to avoid two paths to the same place. */}
            {/* Live-scan door only once a scan actually exists — never advertise
            OBD to the (vast majority of) drivers with no connected dongle. */}
            {scan && (
              <Row
                icon="bluetooth"
                title={t('kol.car.scan', { defaultValue: 'סריקת מצב חי' })}
                sub={t('kol.car.scan_sub', { defaultValue: 'בדיקה חיה של המנוע, המצבר והמוכנות לטסט' })}
                onClick={() => navigate(`/vehicles/${id}/obd`)}
              />
            )}
            <Row
              icon="warning"
              tone="clay"
              title={t('kol.car.accident', { defaultValue: 'דיווח על תאונה' })}
              sub={t('kol.car.accident_sub', {
                defaultValue: 'ברוגע, צעד-צעד: תיעוד, פרטי הצד השני וטופס לחברת הביטוח',
              })}
              onClick={() => navigate(`/vehicles/${id}/accident`)}
            />

            {vehicle?.placeId && (
              <div className="text-center text-[15px] text-kol-ink-faint tracking-[0.06em] tabular-nums pt-1">
                {vehicle.placeId}
              </div>
            )}

            {loading && (
              <div className="text-kol-ink-faint text-center text-[15px]">
                {t('common.loading', { defaultValue: 'טוען…' })}
              </div>
            )}
          </>
        )}
      </div>

      <div className="relative z-[1] px-[22px] pt-3 pb-[max(16px,env(safe-area-inset-bottom))]">
        <VoiceDock
          placeholder={t('kol.car.dock', { defaultValue: 'שאלו אותי משהו…' })}
          onActivate={() => navigate(`/kol/vehicles/${id}/ask`)}
        />
      </div>

      {vehicle && (
        <VehicleSettingsSheet
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          vehicle={vehicle}
          onSaved={() => {
            setSettingsOpen(false);
            if (id)
              getVehicle(id)
                .then(setVehicle)
                .catch(() => undefined);
          }}
          onDeleted={() => navigate('/kol')}
        />
      )}
    </div>
  );
}

function Row({
  icon,
  title,
  sub,
  onClick,
  tone = 'default',
}: {
  icon: IconName;
  title: string;
  sub: string;
  onClick: () => void;
  tone?: 'default' | 'clay';
}) {
  const clay = tone === 'clay';
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'w-full text-start flex items-center gap-3.5 rounded-[20px] p-4 shadow-kol-1 border active:scale-[0.99] transition-transform',
        clay ? 'bg-kol-clay-soft border-[#ecd2c5]' : 'bg-kol-card border-kol-line-soft',
      ].join(' ')}
    >
      <span
        className={`w-11 h-11 rounded-[13px] grid place-items-center flex-none ${clay ? 'bg-[#f0dccb] text-kol-clay-ink' : 'bg-kol-sage-soft text-kol-pine'}`}
      >
        <Icon name={icon} size={22} />
      </span>
      <div className="flex-1 min-w-0">
        <div className={`font-bold text-[16px] ${clay ? 'text-kol-clay-ink' : 'text-kol-ink'}`}>{title}</div>
        <div className={`text-[15px] mt-0.5 ${clay ? 'text-kol-clay-ink opacity-80' : 'text-kol-ink-faint'}`}>
          {sub}
        </div>
      </div>
      <Icon name="chevron" size={18} className={clay ? 'text-kol-clay-ink' : 'text-kol-ink-faint'} />
    </button>
  );
}
